import { addHours } from 'date-fns';
import React from 'react';
import { graphql, useFragment } from 'react-relay';

import { PromotionProblemEventUpsertDialog_promotion$key } from '../../../relay/__generated__/PromotionProblemEventUpsertDialog_promotion.graphql';
import { PromotionProblemEventUpsertDialog_promotionProblemEventUpsertMutation } from '../../../relay/__generated__/PromotionProblemEventUpsertDialog_promotionProblemEventUpsertMutation.graphql';
import { formatDate } from '../../../utils/date';
import { isNullable } from '../../../utils/is';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import NumberField from '../../core/NumberField';
import Text from '../../core/Text';

const PromotionProblemEventUpsertDialog_promotion = graphql`
  fragment PromotionProblemEventUpsertDialog_promotion on Promotion {
    id

    nextOccurrences(limit: 1)
    startAt

    problemEvent {
      durationHours
      durationDays
      count
      offsetHours
    }
  }
`;

type Props = {
  promotion: PromotionProblemEventUpsertDialog_promotion$key;
} & DialogProps &
  Pick<
    MutationFormikProps<PromotionProblemEventUpsertDialog_promotionProblemEventUpsertMutation>,
    'connections' | 'config' | 'onSubmit'
  >;

const PromotionProblemEventUpsertDialog = ({ promotion, connections, config, onSubmit, ...props }: Props) => {
  const { id, problemEvent, nextOccurrences, startAt } = useFragment(
    PromotionProblemEventUpsertDialog_promotion,
    promotion,
  );

  const startDate = new Date(nextOccurrences?.[0] || startAt);

  const dialogActionText = problemEvent ? '수정하기' : '입력하기';

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontWeight: 'bold', fontSize: 3 }}>문제 이벤트 {dialogActionText}</Text>
      </Dialog.Header>
      <MutationFormik<PromotionProblemEventUpsertDialog_promotionProblemEventUpsertMutation>
        mutation={graphql`
          mutation PromotionProblemEventUpsertDialog_promotionProblemEventUpsertMutation(
            $input: PromotionProblemEventUpsertInput!
          ) {
            promotionProblemEventUpsert(input: $input) {
              durationHours
              durationDays
              count
              offsetHours
            }
          }
        `}
        initialValues={{
          promotionId: id,
          offsetHours: 0,

          durationHours: 0,
          durationDays: 0,
          count: 1,
          ...problemEvent,
        }}
        connections={connections}
        config={config}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, handleSubmit, errors }, { nonFieldErrorRef, scrollContainerRef }) => {
          const isDurationError = errors.durationDays ?? errors.durationHours;
          const duration = Number(values?.durationDays ?? 0) * 24 + Number(values?.durationHours ?? 0);
          return (
            <>
              <Dialog.Body ref={scrollContainerRef}>
                <NonFieldError ref={nonFieldErrorRef} sx={{ marginBottom: 3 }} />
                <FormLayout>
                  <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>문제 이벤트 정보</Text>
                  <NumberField
                    min={0}
                    label={'문제 수'}
                    name={'count'}
                    placeholder={'문제 수 입력'}
                    required
                    validate={(v) => {
                      if (isNullable(v) || Number(v) < 1) return '1 이상의 숫자를 입력해주세요';
                    }}
                  />
                  <Grid gapX={2}>
                    <Grid.Unit size={1 / 2}>
                      <NumberField
                        min={0}
                        label={'오프셋 (시간)'}
                        name={'offsetHours'}
                        caption={`${formatDate(
                          addHours(startDate, Number(values?.offsetHours ?? 0)).toISOString(),
                          'P a hh:mm',
                        )}에 시작돼요`}
                        placeholder={'오프셋 입력'}
                        required
                        validate={(v) => {
                          if (isNullable(v)) return '0 이상의 숫자를 입력해주세요';
                        }}
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <NumberField
                        min={0}
                        label={'지속시간 *'}
                        name={'durationDays'}
                        value={values.durationDays ? `${values.durationDays}` : ''}
                        placeholder={'일 입력'}
                        caption={`${formatDate(
                          addHours(startDate, Number(values?.offsetHours ?? 0) + duration).toISOString(),
                          'P a hh:mm',
                        )}에 종료돼요`}
                        validate={() => {
                          if (duration < 1) return '1시간 이상으로 입력해주세요';
                        }}
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <NumberField
                        min={0}
                        label={'지속시간 - 시간'}
                        labelConfig={{ sx: { visibility: 'hidden' } }}
                        name={'durationHours'}
                        value={values.durationHours ? `${values.durationHours}` : ''}
                        placeholder={'시간 입력'}
                        validationStatus={!!isDurationError ? 'error' : undefined}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            setFieldValue('durationHours', undefined);
                            return;
                          }

                          const value = Number(e.target.value);
                          if (value / 24 >= 1) {
                            const day = Math.floor(value / 24) + Number(values.durationDays ?? 0);
                            const hour = value % 24;
                            setFieldValue('durationDays', day);
                            setFieldValue('durationHours', hour);
                          } else {
                            setFieldValue('durationHours', value);
                          }
                        }}
                      />
                    </Grid.Unit>
                  </Grid>
                </FormLayout>
              </Dialog.Body>
              <Dialog.Footer>
                <Button size={'large'} variant={'primary'} onClick={() => handleSubmit()}>
                  {dialogActionText}
                </Button>
              </Dialog.Footer>
            </>
          );
        }}
      </MutationFormik>
    </Dialog>
  );
};

export default PromotionProblemEventUpsertDialog;
